
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";
import { AlertCircle } from "lucide-react";

interface SignInFormProps {
  onToggleMode: () => void;
}

export function SignInForm({ onToggleMode }: SignInFormProps) {
  const { isLoading, handleSignIn } = useAuth();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/^[^@]+@[^@]+\.[^@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email";
    }
    
    if (!formData.password) {
      newErrors.password = "Password is required";
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      handleSignIn(formData);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="email" className="text-white">
          Email
        </Label>
        <Input
          id="email"
          type="text"
          placeholder="m@example.com"
          value={formData.email}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, email: e.target.value }))
          }
          required
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        {errors.email && (
          <div className="mt-1 text-sm font-medium bg-destructive/10 p-2 rounded-md border border-destructive/20 text-black">
            <span className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.email}
            </span>
          </div>
        )}
      </div>
      <div className="space-y-2">
        <Label htmlFor="password" className="text-white">
          Password
        </Label>
        <Input
          id="password"
          type="password"
          placeholder="••••••••"
          value={formData.password}
          onChange={(e) =>
            setFormData((prev) => ({ ...prev, password: e.target.value }))
          }
          required
          className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
        />
        {errors.password && (
          <div className="mt-1 text-sm font-medium bg-destructive/10 p-2 rounded-md border border-destructive/20 text-black">
            <span className="flex items-center gap-1">
              <AlertCircle className="h-4 w-4" />
              {errors.password}
            </span>
          </div>
        )}
      </div>

      <Button
        type="submit"
        className="w-full"
        disabled={isLoading || !formData.email || !formData.password}
      >
        {isLoading ? "Loading..." : "Sign in"}
      </Button>

      <Button
        type="button"
        variant="ghost"
        className="w-full text-white hover:text-white hover:bg-white/10"
        onClick={onToggleMode}
      >
        Don't have an account? Sign up
      </Button>
    </form>
  );
}
